import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { getNewOfferCount, markOffersViewed } from '../api/offers';

export const offerCountQueryKeys = {
  newCount: () => ['offers', 'new-count'] as const,
};

interface UseNewOfferCountOptions {
  enablePolling?: boolean;
}

/**
 * Hook to get count of new offers (buy offers + incoming transfers) since last viewed.
 * Used for nav badge on My Wallet link.
 */
export function useNewOfferCount(options: UseNewOfferCountOptions = {}) {
  const { enablePolling = true } = options;
  const { isAuthenticated } = useAuthStore();

  const query = useQuery({
    queryKey: offerCountQueryKeys.newCount(),
    queryFn: getNewOfferCount,
    staleTime: 30000,
    refetchInterval: enablePolling ? 30000 : false,
    refetchOnWindowFocus: true,
    enabled: isAuthenticated,
  });

  return {
    ...query,
    newOfferCount: query.data ?? 0,
  };
}

/**
 * Hook to mark offers as viewed (clears the badge).
 * Call when user views their wallet offers tab.
 */
export function useMarkOffersViewed() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markOffersViewed,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: offerCountQueryKeys.newCount() });
    },
  });
}
